// Shared South-Africa (ZAR) display resolution for funnel surfaces.
// ONE implementation of the fetch/parse/fail-safe logic that was previously
// duplicated inline on the checkout and success pages (skill:
// docs/agent-skills/mdb-crm-funnel-money-display/SKILL.md). New surfaces that
// render buyer-facing prices consume THIS module — never re-fetch or re-parse
// /geo/regional-pricing ad hoc.
//
// Semantics (binding):
// - DISPLAY-ONLY. Nothing returned here may ever feed a charge amount; the
//   server re-derives the trusted country at every charge path.
// - The endpoint is trusted-ZA-gated server-side (middleware.TrustedCountry on
//   the real client IP): a non-ZA visitor gets region:"" and this resolves to
//   null — surfaces then render standard pricing unchanged.
// - Progressive enhancement: ANY failure (network, shape, empty) returns
//   null/fallbacks; a funnel surface must keep working in USD without it.

// Payload types are GENERATED from the Go source of truth
// (services/api/internal/apicontract → scripts/gen-contracts.sh) — never
// hand-typed twice. Re-exported here so consumers keep one import site.
import type { RegionalPricingItem } from "@/lib/api/contracts.gen";
export type { RegionalPricingItem };

export interface SaDisplay {
  active: true;
  /** ZAR-per-USD rate for incidental FX amounts (add-ons, totals without a curated value). */
  rate: number;
  /** Owner-curated ZAR display cents by product id. */
  zarByPid: Record<string, number>;
  /** Reduced USD charge cents by product id. */
  usdByPid: Record<string, number>;
}

/** Fallback ZAR/USD rate when the rates endpoint is unavailable — keep in sync
 *  with the server fallback in services/api/internal/pricing/rates.go. */
export const ZAR_FALLBACK_RATE = 16.25;

/**
 * Resolves SA (ZAR) display pricing for the given products. Returns null for
 * non-ZA visitors, empty carts, and every error path — callers treat null as
 * "render standard pricing". `rate` starts at ZAR_FALLBACK_RATE; refine it with
 * fetchZarRate() when FX-converted incidental amounts will be shown.
 */
export async function fetchRegionalPricing(
  apiBase: string,
  productIds: (string | undefined | null)[],
): Promise<SaDisplay | null> {
  try {
    const ids = productIds.filter((id): id is string => !!id);
    if (!ids.length) return null;
    const res = await fetch(
      `${apiBase}/api/v1/geo/regional-pricing?product_ids=${encodeURIComponent(ids.join(","))}`,
    );
    if (!res.ok) return null;
    const data = (await res.json())?.data;
    const items: RegionalPricingItem[] =
      data?.region === "ZA" && Array.isArray(data.items) ? data.items : [];
    if (!items.length) return null;
    // Object.create(null): prototype-free maps so a hostile/odd product id like
    // "constructor" can never resolve to an inherited function value.
    const zarByPid: Record<string, number> = Object.create(null);
    const usdByPid: Record<string, number> = Object.create(null);
    for (const it of items) {
      zarByPid[it.product_id] = it.zar_display_cents;
      usdByPid[it.product_id] = it.usd_charge_cents;
    }
    return { active: true, rate: ZAR_FALLBACK_RATE, zarByPid, usdByPid };
  } catch {
    return null; // SA display is progressive enhancement — never break the surface
  }
}

/** Current ZAR/USD rate from the shared rates endpoint; falls back silently. */
export async function fetchZarRate(apiBase: string): Promise<number> {
  try {
    const res = await fetch(`${apiBase}/api/v1/geo/currency`);
    if (!res.ok) return ZAR_FALLBACK_RATE;
    const r = (await res.json())?.data?.rates?.ZAR;
    return typeof r === "number" && r > 0 ? r : ZAR_FALLBACK_RATE;
  } catch {
    return ZAR_FALLBACK_RATE;
  }
}

/**
 * Display cents for one line: the owner's curated ZAR when the product is
 * catalogued, FX-ZAR for incidental amounts, or the raw cents when not in SA
 * mode. Pure display math — never feed the result into a charge.
 */
export function saDisplayCents(
  cents: number,
  sa: SaDisplay | null,
  productId?: string,
): number {
  if (sa?.active) {
    if (productId && sa.zarByPid[productId] != null) return sa.zarByPid[productId];
    return Math.round(cents * sa.rate);
  }
  return cents;
}
